import { getBackendUrl } from "@Utils";
import axiosWrapper from "../services/axiosWrapper";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "../services/constants";
import type { SaveProjectsRequest, SaveProjectsResponse } from "@ServerTypes/projects/project.schema";
import type {
  GetCommentsRequest,
  GetCommentsResponse,
  SaveCommentsRequest,
  SaveCommentsResponse,
  DeleteCommentsRequest,
  DeleteCommentsResponse,
} from "@ServerTypes/comments/comments.schema.js";
import type {
  GetIssuesDataRequest,
  GetIssuesDataResponse,
  GetIssuesPinDataRequest,
  GetIssuesPinDataResponse,
  GetIssueViewpointDataRequest,
  GetIssueViewpointDataResponse,
  GetIssuesDataByPortalRequest,
  GetIssuesDataByPortalResponse,
  RemoveIssueFromFormRequest,
  RemoveIssueFromFormResponse,
  AddFormIdToIssuesRequest,
  AddFormIdToIssuesResponse,
} from "@ServerTypes/issues/issues.schema.js";
import { defaultListOfStatusAvailableUrl } from "../services/dataManager";
import { showToastNotification } from "../reducers/toastSlice";
import { setIssuesCount } from "../reducers/issuesCount";
import { setTasksStatusesData } from "../reducers/tasksStatuses";
import { setFilesStatusesData } from "../reducers/filesStatuses";
import { setProjectsStatusesData } from "../reducers/projectsStatuses";
import { setItemsHaveError, setItemsAreLoading } from "../reducers/bcfData";
import { setIssuesPinData } from "../reducers/issuesPinDataSlice";
import {
  updateIssuesData as arrangeIssuesData,
  setIssuesData,
  modifyIssuesData,
  setIssuesCommentsData,
  deleteIssuesCommentsData,
  mergeIssuesData as _mergeIssuesData,
  removeIssueFromState,
} from "../reducers/issues";
import { t, untranslated } from "@i18n";
import { EXPORT_TYPE, exportPdf } from "@Services/fileDownload";
interface ExportParams {
  projectId: string;
  issuesIds?: string[];
}
export function removeIssueFromForm(ids: string[], formId: string) {
  return async (dispatch: any) => {
    const data: RemoveIssueFromFormRequest = { ids, formId };
    const response = await axiosWrapper.post<RemoveIssueFromFormResponse>(
      `${getBackendUrl()}/issues/removeIssueFromForm`,
      data,
    );
    if (response.data.status) {
      dispatch(removeIssueFromState({ ids }));
      dispatch(
        showToastNotification({
          content: t("All the selected Issue(s) were removed"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    return response.data.status;
  };
}

export function addFormIdToIssues(ids: string[], formId: string) {
  return async (dispatch: any) => {
    const data: AddFormIdToIssuesRequest = { ids, formId };
    const response = await axiosWrapper.post<AddFormIdToIssuesResponse>(
      `${getBackendUrl()}/issues/addFormIdToIssues`,
      data,
    );
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("All the selected Issue(s) were added"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };
}

export function updateTaskData(data: any) {
  return {
    type: "UPDATE_TASK_DATA",
    data,
  };
}
export function removeTaskData(data: any) {
  return {
    type: "REMOVE_TASK_DATA",
    data,
  };
}
export function getCommentsData(data: GetCommentsRequest & { issueId?: string }) {
  return async (dispatch: any) => {
    dispatch(setItemsAreLoading(true));
    const responseData = await axiosWrapper.post<GetCommentsResponse>(
      `${getBackendUrl()}/comments/getCommentsData`,
      data,
    );
    if (responseData.data.status && responseData.data.data) {
      dispatch(setIssuesCommentsData({ data: responseData.data.data, issueId: data.issueId }));
      return responseData.data.data;
    }
  };
}
export function saveCommentsData(data: SaveCommentsRequest & { issueId?: string }) {
  return (dispatch: any) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .post<SaveCommentsResponse>(`${getBackendUrl()}/comments/saveCommentsData`, data)
      .then((response) => {
        if (response.data.status && response.data.data) {
          dispatch(
            setIssuesCommentsData({ data: response.data.data, issueId: data.issueId, isEdit: !!data.commentId }),
          );
        }
      })
      .catch(() => dispatch(setItemsHaveError(true)));
  };
}
export function deleteCommentsData(data: DeleteCommentsRequest & { issueId?: string }) {
  return (dispatch: any) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .post<DeleteCommentsResponse>(`${getBackendUrl()}/comments/deleteCommentsData`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(deleteIssuesCommentsData({ commentId: data.commentId, issueId: data.issueId }));
        }
      })
      .catch(() => dispatch(setItemsHaveError(true)));
  };
}
export const issuesInFilesCount = async (data: GetIssuesDataRequest) => {
  const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
  const result = response.data.result as unknown[] | undefined;
  return result?.length ?? 0;
};

export function getIssuesData(data: GetIssuesDataRequest, returnValue = false) {
  return async (dispatch: any) => {
    const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
    if (response.data.status && response.data.result) {
      dispatch(setIssuesData(response.data.result));
      if (response.data.count) {
        dispatch(setIssuesCount(response.data.count));
      }
    }
    if (returnValue) {
      return response.data.result;
    }
  };
}

export async function getIssuesList(data: GetIssuesDataRequest): Promise<Record<string, unknown>[]> {
  const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
  return (response.data.result as Record<string, unknown>[]) ?? [];
}
export function getIssueViewpointData(data: GetIssueViewpointDataRequest) {
  return async (dispatch: any) => {
    const response = await axiosWrapper.post<GetIssueViewpointDataResponse>(
      `${getBackendUrl()}/issues/getIssueViewpointData`,
      data,
    );
    if (response.data.status && response.data.result) {
      dispatch(modifyIssuesData(response.data.result));
    }
  };
}
export function getfilteredIssuesData(data: GetIssuesDataRequest, returnValue = false) {
  return async (dispatch: any) => {
    const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
    if (response.data.status && response.data.result) {
      dispatch(arrangeIssuesData(response.data.result));
    }
    if (returnValue) {
      return response.data.result;
    }
  };
}
export function getIssuesPinData(data: GetIssuesPinDataRequest) {
  return async (dispatch: any) => {
    const response = await axiosWrapper.post<GetIssuesPinDataResponse>(
      `${getBackendUrl()}/issues/getIssuesPinData`,
      data,
    );
    if (response.data.status && response.data.result) {
      dispatch(setIssuesPinData(response.data.result));
    }
  };
}
// TODO: This function is not used anywhere and backend is also removed
export function getFileData(data: any, returnValue = false) {
  return async () => {
    const response = await axiosWrapper.post(`${getBackendUrl()}/fileOperations/getFileData`, data);
    if (returnValue) {
      return response.data.data;
    }
  };
}
export function getIssuesDataByPortal(data: GetIssuesDataByPortalRequest) {
  return (dispatch: any) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .post<GetIssuesDataByPortalResponse>(`${getBackendUrl()}/issues/getIssuesDataByPortal`, data)
      .then((response) => {
        if (response.data.result) {
          dispatch(setIssuesData(response.data.result));
        }
      })
      .catch(() => dispatch(setItemsHaveError(true)));
  };
}
export async function getCompleteIssuesDataByPortal(data: GetIssuesDataByPortalRequest) {
  const response = await axiosWrapper.post<GetIssuesDataByPortalResponse>(
    `${getBackendUrl()}/issues/getIssuesDataByPortal`,
    data,
  );
  return response.data.result ?? [];
}
export function getTasksStatusesData() {
  return (dispatch: any) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .get(`${getBackendUrl()}/tasks/getAllStatusesForTasks`)
      .then((response: any) => {
        if (response.data.status) {
          dispatch(setTasksStatusesData(response.data.data));
        }
      })
      .catch(() => dispatch(setItemsHaveError(true)));
  };
}
export function getFilesStatusesData() {
  return (dispatch: any) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .get(`${getBackendUrl()}/fileUpload/getAllStatusesForFiles`)
      .then((response: any) => {
        if (response.data.status) {
          dispatch(setFilesStatusesData(response.data.data));
        }
      })
      .catch(() => dispatch(setItemsHaveError(true)));
  };
}
export function getProjectsStatusesData() {
  return (dispatch: any) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .get(`${getBackendUrl()}/projects/getAllStatusesForProjects`)
      .then((response: any) => {
        if (response.data.status) {
          dispatch(setProjectsStatusesData(response.data.data));
        }
      })
      .catch(() => dispatch(setItemsHaveError(true)));
  };
}
export function saveProjectStatus(data: any) {
  return (dispatch: any) => {
    axiosWrapper
      .post(`${getBackendUrl()}/projects/saveProjectStatus`, data)
      .then((response: any) => {
        if (response.data.status) {
          dispatch(
            showToastNotification({
              content: t("Status changed successfully"),
            }),
          );
        }
      })
      .catch(() => dispatch(setItemsHaveError(true)));
  };
}
export function saveProjectAttachedPositionData(data: any) {
  return async (dispatch: any) => {
    const response = await axiosWrapper.post(`${getBackendUrl()}/projects/saveProjectAssignedPosition`, data);
    let toastFields;
    if (response.data.status) {
      toastFields = {
        content: t("Assigned position changed successfully"),
        type: TOAST_TYPE_SUCCESS,
      };
    } else {
      toastFields = {
        content: untranslated(response.data.errorMessage),
        type: TOAST_TYPE_ERROR,
      };
    }
    dispatch(showToastNotification(toastFields));
  };
}
export function saveTaskStatus(data: any) {
  return async (dispatch: any) => {
    await axiosWrapper
      .post(`${getBackendUrl()}/tasks/saveTaskStatus`, data)
      .then((response: any) => {
        if (response.data.status) {
          dispatch(updateTaskData(data));
        }
      })
      .catch(() => dispatch(setItemsHaveError(true)));
  };
}
export async function deleteTask(data: any) {
  const response = await axiosWrapper.post(`${getBackendUrl()}/tasks/deleteTask`, data);
  return response;
}
export async function saveProjectData(data: SaveProjectsRequest): Promise<SaveProjectsResponse> {
  const response = await axiosWrapper.post(`${getBackendUrl()}/projects/saveProjectsData`, data);
  return response.data;
}
export async function getPdfMarkUpData(data: any) {
  const response = await axiosWrapper.post(`${getBackendUrl()}/issues/getPdfMarkUpData`, data);
  return response;
}
export function successReadNotificationToast(selectedNotificationsId: any) {
  return async (dispatch: any) => {
    const suffix = selectedNotificationsId.length > 1 ? "s" : "";
    dispatch(
      showToastNotification({
        content: t(`Notification${suffix} successfully marked as read`),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
  };
}
export async function getCommentsList(data: GetCommentsRequest): Promise<{ data: GetCommentsResponse }> {
  return await axiosWrapper.post<GetCommentsResponse>(`${getBackendUrl()}/comments/getCommentsData`, data);
}
async function getIssuesIdToExport(data: any) {
  return await axiosWrapper.post(`${getBackendUrl()}/issues/getIssuesIdToExport`, data);
}
export async function handleIssuesExportAsPDF({ projectId, issuesIds }: ExportParams) {
  if (!issuesIds) {
    const res = await getIssuesIdToExport({ projectId });
    if (res.data.status) {
      issuesIds = res.data.result;
    } else {
      return res.data;
    }
  }
  if (issuesIds?.length) {
    exportPdf({ issuesIds, exportType: EXPORT_TYPE.PDF });
  } else {
    return {
      status: false,
      errorMessage: "There are no issues in this Project",
    };
  }
}
export const issuesBcfImport = async (data: any) => {
  const response = await axiosWrapper.post(`${getBackendUrl()}/issues/saveIssuesDataFromBCF`, data);
  return response;
};
export const getDefaultListOfStatusAvailable = async ({ spaceId, entityType }: { spaceId: any; entityType: any }) => {
  const data = {
    value: {
      params: {
        entityType,
        spaceId,
      },
      entityType,
      spaceId,
    },
  };
  return await axiosWrapper.post(defaultListOfStatusAvailableUrl, data);
};
